import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const features = [
  {
    number: '01',
    title: 'Sanntids Veikart',
    subtitle: 'Live trafikk & veimeldinger',
    description: 'Interaktivt kart med sanntidsdata fra Statens vegvesen. Se trafikkulykker, vegarbeid, stengte veier og kamerafeed fra hele landet — oppdatert hvert minutt.',
    icon: 'Map',
    color: '#3B6FD4',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_183bf20d8-1772187879983.png',
    imageAlt: 'Aerial view of Norwegian mountain road winding through snow-covered landscape with fjord below',
    tags: ['GPS-sporing', 'Trafikkkamera', 'Vegmeldinger']
  },
  {
    number: '02',
    title: 'Bomkalkulator',
    subtitle: 'Nøyaktig kostnadsberegning',
    description: 'Beregn eksakte bomkostnader for enhver rute i Norge. Støtter alle kjøretøytyper, AutoPASS-rabatter, og viser alternative ruter med prissammenligning.',
    icon: 'Calculator',
    color: '#2EC4A0',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b853c661-1772187880474.png',
    imageAlt: 'Norwegian toll station on highway with mountains in background during golden hour',
    tags: ['AutoPASS', 'Alle kjøretøy', 'Rute-alternativ']
  },
  {
    number: '03',
    title: 'Kjøretøyregister',
    subtitle: 'Full kontroll over din flåte',
    description: 'Administrer opptil 5 kjøretøy under ett abonnement. Automatisk rabattberegning basert på utslippsklasse, og komplett historikk for hvert kjøretøy.',
    icon: 'Car',
    color: '#E8941A',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bffec7ea-1772187879348.png',
    imageAlt: 'Modern electric car driving on scenic Norwegian coastal road with fjord and mountains',
    tags: ['Opptil 5 biler', 'EL-rabatt', 'Historikk']
  }
];

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const feat = features?.[activeFeature];

  return (
    <section style={{ background: '#080D14', padding: '80px 0 100px', position: 'relative', overflow: 'hidden' }}>
      {/* Section label — rotated, far left — hidden on mobile */}
      <div className="features-side-label" style={{
        position: 'absolute', left: '-20px', top: '50%',
        transform: 'translateY(-50%) rotate(-90deg)',
        fontFamily: 'monospace', fontSize: '0.55rem',
        letterSpacing: '0.4em', color: 'rgba(59,111,212,0.25)',
        textTransform: 'uppercase', whiteSpace: 'nowrap'
      }}>TJENESTER — 02</div>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%' }}>

        {/* Header — brutalist, left-heavy */}
        <div style={{ marginBottom: '50px', position: 'relative' }}>
          <div style={{
            fontFamily: '"Arial Black", Impact, sans-serif',
            fontSize: 'clamp(0.6rem, 1vw, 0.75rem)',
            letterSpacing: '0.35em', color: '#3B6FD4',
            textTransform: 'uppercase', marginBottom: '12px'
          }}>— Hva vi tilbyr</div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '24px', flexWrap: 'wrap' }}>
            <h2 style={{
              fontFamily: '"Arial Black", Impact, sans-serif',
              fontSize: 'clamp(2rem, 6vw, 5.5rem)',
              fontWeight: 900, color: '#EEF0F2',
              letterSpacing: '-0.04em', lineHeight: 0.9,
              margin: 0, textTransform: 'uppercase'
            }}>ALT DU TRENGER</h2>
            <span style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 'clamp(1.2rem, 3vw, 2.8rem)',
              color: 'rgba(238,240,242,0.25)',
              letterSpacing: '-0.02em', lineHeight: 1,
              paddingBottom: '8px'
            }}>på norske veier</span>
          </div>

          {/* Underline — hand-drawn feel */}
          <div style={{
            marginTop: '16px',
            width: 'clamp(120px, 20vw, 280px)',
            height: '3px',
            background: `linear-gradient(to right, #3B6FD4, transparent)`,
            borderRadius: '2px'
          }} />
        </div>

        {/* Feature tabs — scrollable on mobile */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '40px', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {features?.map((f, i) => (
            <button
              key={i}
              onClick={() => setActiveFeature(i)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '14px 20px',
                borderBottom: activeFeature === i ? `3px solid ${f?.color}` : '3px solid transparent',
                fontFamily: activeFeature === i ? '"Arial Black", sans-serif' : 'Georgia, serif',
                fontStyle: activeFeature === i ? 'normal' : 'italic',
                fontSize: activeFeature === i ? '0.8rem' : '0.9rem',
                fontWeight: activeFeature === i ? 900 : 400,
                color: activeFeature === i ? '#EEF0F2' : 'rgba(138,149,163,0.5)',
                letterSpacing: activeFeature === i ? '0.06em' : '0',
                textTransform: activeFeature === i ? 'uppercase' : 'none',
                transition: 'all 200ms ease',
                marginBottom: '-1px',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              {f?.title}
            </button>
          ))}
        </div>

        {/* Feature content — responsive grid */}
        <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'start' }}>

          {/* Left: image with overlapping text */}
          <div style={{ position: 'relative' }}>
            {/* Polaroid-style frame */}
            <div style={{
              background: '#1a1a1a',
              padding: '12px 12px 48px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
              transform: 'rotate(-1.5deg)',
              position: 'relative'
            }}>
              <img
                src={feat?.image}
                alt={feat?.imageAlt}
                style={{
                  width: '100%', height: '280px',
                  objectFit: 'cover',
                  display: 'block',
                  filter: 'brightness(0.75) contrast(1.1) saturate(0.8)'
                }}
              />
              <div style={{
                position: 'absolute', bottom: '10px', left: 0, right: 0,
                textAlign: 'center',
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
                fontSize: '0.8rem', color: 'rgba(238,240,242,0.5)',
                letterSpacing: '0.02em'
              }}>{feat?.subtitle}</div>
            </div>

            {/* Giant number — breaks out of polaroid */}
            <div style={{
              position: 'absolute', top: '-30px', right: '-20px',
              fontFamily: '"Arial Black", Impact, sans-serif',
              fontSize: '9rem', fontWeight: 900,
              color: `${feat?.color}12`,
              letterSpacing: '-0.08em', lineHeight: 1,
              pointerEvents: 'none', userSelect: 'none'
            }}>{feat?.number}</div>

            {/* Tag badges */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
              {feat?.tags?.map((tag, ti) => (
                <span key={ti} style={{
                  fontFamily: 'monospace',
                  fontSize: '0.6rem', letterSpacing: '0.15em',
                  color: feat?.color,
                  border: `1px solid ${feat?.color}40`,
                  padding: '4px 10px',
                  textTransform: 'uppercase',
                  transform: ti === 1 ? 'rotate(-1deg)' : ti === 2 ? 'rotate(0.5deg)' : 'none',
                  display: 'inline-block'
                }}>{tag}</span>
              ))}
            </div>
          </div>

          {/* Right: text content — editorial */}
          <div style={{ paddingTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
              <div style={{
                width: '48px', height: '48px',
                background: `${feat?.color}15`,
                border: `1px solid ${feat?.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon name={feat?.icon} size={20} color={feat?.color} />
              </div>
              <div style={{
                fontFamily: 'monospace', fontSize: '0.6rem',
                letterSpacing: '0.2em', color: `${feat?.color}80`,
                textTransform: 'uppercase'
              }}>Funksjon {feat?.number}</div>
            </div>

            <h3 style={{
              fontFamily: '"Arial Black", Impact, sans-serif',
              fontSize: 'clamp(1.6rem, 3.5vw, 3rem)',
              fontWeight: 900, color: '#EEF0F2',
              letterSpacing: '-0.03em', lineHeight: 0.92,
              textTransform: 'uppercase', margin: '0 0 8px'
            }}>{feat?.title}</h3>

            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: '1rem', color: feat?.color,
              marginBottom: '28px', letterSpacing: '0.01em'
            }}>{feat?.subtitle}</div>

            <p style={{
              fontFamily: 'Georgia, serif',
              fontSize: '1rem', color: 'rgba(238,240,242,0.7)',
              lineHeight: 1.8, margin: '0 0 32px'
            }}>{feat?.description}</p>

            <div style={{
              borderLeft: `3px solid ${feat?.color}`,
              paddingLeft: '16px',
              marginBottom: '32px'
            }}>
              <div style={{
                fontFamily: 'Georgia, serif', fontStyle: 'italic',
                fontSize: '0.85rem', color: 'rgba(138,149,163,0.7)',
                lineHeight: 1.6
              }}>Oppdateres hvert minutt fra Statens vegvesen</div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {features?.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActiveFeature(i)}
                  style={{
                    width: activeFeature === i ? '28px' : '8px',
                    height: '8px',
                    background: activeFeature === i ? f?.color : 'rgba(255,255,255,0.15)',
                    border: 'none', cursor: 'pointer',
                    transition: 'all 300ms ease',
                    borderRadius: '0'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 767px) {
          .features-grid { grid-template-columns: 1fr !important; }
          .features-side-label { display: none !important; }
        }
      `}</style>
    </section>
  );
};

export default FeaturesSection;